import React, { useState, useEffect } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./CreateUserModal.css";

export default function CreateUserModal({ show, onClose, onSubmit }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [policy, setPolicy] = useState("");
  const [role, setRole] = useState("sub");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [strength, setStrength] = useState("");
  const [error, setError] = useState("");
  const [formInstanceKey, setFormInstanceKey] = useState(0);
  const [unlockFields, setUnlockFields] = useState(false);

  function resetForm() {
    setEmail("");
    setFirstName("");
    setLastName("");
    setPassword("");
    setConfirmPassword("");
    setPolicy("");
    setRole("sub");
    setShowPwd(false);
    setShowConfirm(false);
    setStrength("");
    setError("");
    setUnlockFields(false);
  }

  useEffect(() => {
    if (!show) return;

    setFormInstanceKey((value) => value + 1);
    resetForm();

    const timers = [30, 180, 500].map((delay) =>
      window.setTimeout(() => resetForm(), delay)
    );

    return () => timers.forEach((timerId) => window.clearTimeout(timerId));
  }, [show]);

  function releaseAutofillLock() {
    setUnlockFields(true);
  }

  useEffect(() => {
    if (!password) { setStrength(""); return; }
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    if (score <= 2) setStrength("Fraca");
    else if (score < 5) setStrength("Média");
    else setStrength("Forte");
  }, [password]);

  const colorMap = { Fraca: "#dc3545", Média: "#ffc107", Forte: "#198754" };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Submitting with state:", { email, firstName, lastName, password, confirmPassword, policy, role });
    let missing = [];
    if (!email) missing.push('E-mail');
    if (!firstName) missing.push('Nome');
    if (!lastName) missing.push('Sobrenome');
    if (!password) missing.push('Senha');
    if (!confirmPassword) missing.push('Confirmação de senha');
    if (!policy) missing.push('Política');
    if (missing.length) {
      setError(`Preencha: ${missing.join(', ')}`);
      return;
    }
    if (password !== confirmPassword) {
      setError('As senhas não conferem.');
      return;
    }
    setError("");
    const result = await onSubmit({ email, firstName, lastName, password, policy, role });
    if (!result?.ok) {
      setError(result?.error || "Erro ao criar usuário.");
      return;
    }

    resetForm();
    onClose();
  };

  if (!show) return null;

  return (
    <div className="modal d-block bg-backdrop create-user-modal">
      <div className="modal-dialog modal-lg">
        <form
          key={formInstanceKey}
          className="modal-content"
          onSubmit={handleSubmit}
          autoComplete="off"
        >
          <div className="modal-header bg-white">
            <h5 className="modal-title text-dark">Criar Sub-Usuário</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body bg-white">
            {error && <div className="alert alert-danger mb-3">{error}</div>}

            {/* Campos-isca para reduzir autofill do navegador no modal */}
            <input
              type="text"
              name="fake-username"
              autoComplete="username"
              tabIndex="-1"
              className="autofill-decoy"
            />
            <input
              type="password"
              name="fake-password"
              autoComplete="current-password"
              tabIndex="-1"
              className="autofill-decoy"
            />

            {/* E-mail */}
            <div className="row mb-3 align-items-center">
              <label className="col-sm-3 col-form-label text-end">E-mail:</label>
              <div className="col-sm-9">
                <input
                  type="text"
                  className="form-control"
                  name="subuser-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="exemplo@dominio.com"
                  autoComplete="new-password"
                  inputMode="email"
                  spellCheck="false"
                  readOnly={!unlockFields}
                  onFocus={releaseAutofillLock}
                  onMouseDown={releaseAutofillLock}
                />
              </div>
            </div>

            {/* Nome */}
            <div className="row mb-3 align-items-center">
              <label className="col-sm-3 col-form-label text-end">Nome:</label>
              <div className="col-sm-9">
                <input
                  type="text"
                  className="form-control"
                  name="subuser-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Primeiro nome"
                  autoComplete="new-password"
                  readOnly={!unlockFields}
                  onFocus={releaseAutofillLock}
                  onMouseDown={releaseAutofillLock}
                />
              </div>
            </div>

            {/* Sobrenome */}
            <div className="row mb-3 align-items-center">
              <label className="col-sm-3 col-form-label text-end">Sobrenome:</label>
              <div className="col-sm-9">
                <input
                  type="text"
                  className="form-control"
                  name="subuser-last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Último nome"
                  autoComplete="new-password"
                  readOnly={!unlockFields}
                  onFocus={releaseAutofillLock}
                  onMouseDown={releaseAutofillLock}
                />
              </div>
            </div>

            {/* Senha */}
            <div className="row mb-3 align-items-center">
              <label className="col-sm-3 col-form-label text-end">Senha:</label>
              <div className="col-sm-9 position-relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  className="form-control pe-5"
                  name="subuser-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite a senha"
                  autoComplete="new-password"
                  readOnly={!unlockFields}
                  onFocus={releaseAutofillLock}
                  onMouseDown={releaseAutofillLock}
                />
                <button
                  type="button"
                  className="pwd-toggle"
                  aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                  onClick={() => setShowPwd((v) => !v)}
                >
                  {showPwd ? <FaEyeSlash /> : <FaEye />}
                </button>
                <div className="form-text ps-sm-3">
                  Mínimo 8 caracteres, inclua maiúsculas, minúsculas, números e símbolos.
                </div>
                {strength && (
                  <div className="ps-sm-3 mt-1" style={{ color: colorMap[strength], fontWeight: 600 }}>
                    Força da senha: {strength}
                  </div>
                )}
              </div>
            </div>

            {/* Confirmar Senha */}
            <div className="row mb-3 align-items-center">
              <label className="col-sm-3 col-form-label text-end">Confirmar Senha:</label>
              <div className="col-sm-9 position-relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  className="form-control pe-5"
                  name="subuser-password-confirmation"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repita a senha"
                  autoComplete="new-password"
                  readOnly={!unlockFields}
                  onFocus={releaseAutofillLock}
                  onMouseDown={releaseAutofillLock}
                />
                <button
                  type="button"
                  className="pwd-toggle"
                  aria-label={showConfirm ? "Ocultar confirmação de senha" : "Mostrar confirmação de senha"}
                  onClick={() => setShowConfirm((v) => !v)}
                >
                  {showConfirm ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* Política */}
            <div className="row mb-3 align-items-center">
              <label className="col-sm-3 col-form-label text-end">Política:</label>
              <div className="col-sm-9">
                <select
                  className="form-select"
                  value={policy}
                  onChange={(e) => setPolicy(e.target.value)}
                >
                  <option value="">Selecione...</option>
                  <option value="consoleAdmin">ConsoleAdmin</option>
                  <option value="admin">Admin</option>
                  <option value="full">Leitura/Gravação</option>
                  <option value="readonly">Somente Leitura</option>
                  <option value="writeonly">Somente Gravação</option>
                </select>
              </div>
            </div>

            {/* Perfil */}
            <div className="row mb-3 align-items-center">
              <label className="col-sm-3 col-form-label text-end">Perfil:</label>
              <div className="col-sm-9">
                <select
                  className="form-select"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <option value="sub">Usuário</option>
                  <option value="owner">Administrador</option>
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer bg-white">
            <button type="button" className="btn btn-secondary me-2" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-viadrive">
              Criar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
